import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { AlertCircle, CheckCircle2, ClipboardList, MapPin, Play, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PageHeader, LoadingState, StatusBadge, DataTable, type Column } from '@/components/common';
import { DetailLayout, DetailList, RailCard, StatStrip } from '@/components/common/DetailPanels';
import { yardApi, type YardPickList, type YardPickTask } from '@/api/services/yard.api';
import { formatDateTime, formatNumber, toTitleCase } from '@/lib/utils';
import { getErrorMessage } from '@/api/client';

/** Priority is stored 1–10; the yard reads it in three bands. */
function priorityBand(priority: number) {
  if (priority <= 3) return { label: 'High', variant: 'destructive' as const };
  if (priority <= 6) return { label: 'Medium', variant: 'outline' as const };
  return { label: 'Low', variant: 'secondary' as const };
}

/**
 * One pick list — its tasks, and the picking itself.
 *
 * The list is a document the yard works through over a shift, often handed
 * between people. Picking happened inside a sheet, so the person picking had no
 * address for what they were working on and no way to come back to it.
 */
export default function YardPickListDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pickList, setPickList] = useState<YardPickList | null>(null);
  const [loading, setLoading] = useState(true);
  const [taskToComplete, setTaskToComplete] = useState<YardPickTask | null>(null);
  const [form, setForm] = useState({ pickedQty: '', notes: '' });

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setPickList(await yardApi.picking.getById(id));
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/yard/picking');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const startPicking = async () => {
    if (!pickList) return;
    try {
      await yardApi.picking.start(pickList.id);
      toast.success('Picking started');
      load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const completePickList = async () => {
    if (!pickList) return;
    try {
      await yardApi.picking.complete(pickList.id);
      toast.success('Pick list completed');
      load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const openTask = (task: YardPickTask) => {
    setTaskToComplete(task);
    setForm({ pickedQty: task.requestedQty.toString(), notes: '' });
  };

  const completeTask = async () => {
    if (!taskToComplete || !pickList) return;
    const pickedQty = parseFloat(form.pickedQty);
    if (isNaN(pickedQty) || pickedQty < 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    try {
      await yardApi.picking.completeTask(pickList.id, taskToComplete.id, {
        pickedQty,
        notes: form.notes || undefined,
      });
      toast.success('Task completed');
      setTaskToComplete(null);
      load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (loading) return <LoadingState variant="detail" />;
  if (!pickList) return null;

  const tasks = [...(pickList.lines ?? [])].sort((a, b) => a.sequence - b.sequence);
  const done = tasks.filter((t) => t.status === 'COMPLETED').length;
  const progress = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
  const requested = tasks.reduce((sum, t) => sum + t.requestedQty, 0);
  const picked = tasks.reduce((sum, t) => sum + t.pickedQty, 0);
  const band = priorityBand(pickList.priority);
  const picking = pickList.status === 'IN_PROGRESS';

  const taskColumns: Column<YardPickTask>[] = [
    {
      key: 'sequence',
      header: '#',
      sortable: true,
      className: 'w-[56px]',
      render: (t) => <span className="tabular-nums text-muted-foreground">{t.sequence}</span>,
    },
    {
      key: 'item',
      header: 'Item',
      linkTo: (t) => `/inventory/${t.itemId}`,
      render: (t) => (
        <div>
          <p className="font-medium">{t.item?.name ?? 'Unknown item'}</p>
          <p className="text-xs text-muted-foreground">{t.item?.sku}</p>
        </div>
      ),
    },
    {
      key: 'storageLocation',
      header: 'From',
      render: (t) => (
        <span className="flex items-center gap-1.5 text-sm">
          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
          {t.storageLocation?.code ?? 'No location'}
        </span>
      ),
    },
    {
      key: 'pickedQty',
      header: 'Picked',
      sortable: true,
      className: 'text-right',
      render: (t) => (
        // A completed task that came up short is the thing a supervisor needs
        // to see; the pair of figures says it without a second column.
        <span
          className={`block text-right tabular-nums ${
            t.status === 'COMPLETED' && t.pickedQty < t.requestedQty
              ? 'text-status-warning-foreground'
              : ''
          }`}
        >
          {formatNumber(t.pickedQty)} / {formatNumber(t.requestedQty)} {t.item?.unit}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (t) => <StatusBadge status={t.status} />,
    },
    {
      key: 'pick',
      header: '',
      className: 'w-[92px]',
      render: (t) =>
        picking && t.status !== 'COMPLETED' ? (
          <Button size="sm" onClick={() => openTask(t)}>
            <CheckCircle2 className="mr-1 h-4 w-4" />
            Pick
          </Button>
        ) : null,
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Picking', href: '/yard/picking' },
            { label: pickList.pickListNumber },
          ]}
          title={pickList.pickListNumber}
          description={`${tasks.length} tasks from ${toTitleCase(pickList.sourceType)}`}
          titleAdornment={
            <div className="flex gap-2">
              <StatusBadge status={pickList.status} />
              <Badge variant={band.variant}>{band.label}</Badge>
            </div>
          }
          actions={
            pickList.status === 'PENDING' ? (
              <Button onClick={startPicking}>
                <Play className="mr-2 h-4 w-4" />
                Start picking
              </Button>
            ) : picking ? (
              <Button onClick={completePickList}>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Complete pick list
              </Button>
            ) : undefined
          }
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <DetailLayout
          rail={
            <>
              <RailCard
                title="Assignment"
                icon={<UserRound className="h-4 w-4 text-muted-foreground" />}
              >
                <DetailList
                  items={[
                    {
                      label: 'Assigned to',
                      value: pickList.assignedTo?.fullName ?? 'Unassigned',
                      tone: pickList.assignedTo ? 'default' : 'warning',
                    },
                    { label: 'Priority', value: `${band.label} (${pickList.priority})` },
                    {
                      label: 'Started',
                      value: pickList.startedAt ? formatDateTime(pickList.startedAt) : null,
                      when: !!pickList.startedAt,
                    },
                    {
                      label: 'Completed',
                      value: pickList.completedAt ? formatDateTime(pickList.completedAt) : null,
                      when: !!pickList.completedAt,
                    },
                  ]}
                />
              </RailCard>

              <RailCard
                title="Source"
                icon={<ClipboardList className="h-4 w-4 text-muted-foreground" />}
              >
                <DetailList
                  items={[
                    { label: 'Type', value: toTitleCase(pickList.sourceType) },
                    {
                      label: 'Kit',
                      // Only job kits have somewhere to go; other sources are
                      // recorded by id alone, so they get no dead link.
                      value:
                        pickList.sourceType === 'JOB_KIT' ? (
                          <Link
                            to={`/yard/job-kits/${pickList.sourceId}`}
                            className="underline-offset-4 hover:underline"
                          >
                            Open kit
                          </Link>
                        ) : null,
                      when: pickList.sourceType === 'JOB_KIT',
                    },
                    { label: 'Tasks', value: tasks.length },
                    {
                      label: 'Short picked',
                      value: formatNumber(requested - picked),
                      tone: 'warning',
                      when: done === tasks.length && tasks.length > 0 && picked < requested,
                    },
                  ]}
                />
              </RailCard>
            </>
          }
          main={
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Progress</h3>
                  <CardDescription>
                    {done} of {tasks.length} tasks picked
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Tasks completed</span>
                      <span className="font-medium tabular-nums">{progress}%</span>
                    </div>
                    <Progress value={progress} />
                  </div>
                  <StatStrip
                    stats={[
                      { label: 'Requested', value: formatNumber(requested) },
                      { label: 'Picked', value: formatNumber(picked) },
                      { label: 'Tasks', value: `${done} / ${tasks.length}` },
                    ]}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Pick tasks</h3>
                  <CardDescription>
                    In pick sequence, the walking order of the yard
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <DataTable<YardPickTask>
                    pagination
                    data={tasks}
                    columns={taskColumns}
                    emptyTitle="No tasks"
                    emptyDescription="This pick list has no tasks on it."
                  />
                </CardContent>
              </Card>

              {pickList.notes && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Notes</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{pickList.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          }
        />
      </motion.div>

      <Dialog open={!!taskToComplete} onOpenChange={(open) => !open && setTaskToComplete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete pick task</DialogTitle>
            <DialogDescription>Enter the quantity picked from the location.</DialogDescription>
          </DialogHeader>
          {taskToComplete && (
            <div className="space-y-4 py-4">
              <div className="space-y-2 rounded-lg bg-muted p-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Item</span>
                  <span className="font-medium">{taskToComplete.item?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Location</span>
                  <span>{taskToComplete.storageLocation?.code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Requested</span>
                  <span className="tabular-nums">{taskToComplete.requestedQty}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pickedQty">Picked quantity</Label>
                <Input
                  id="pickedQty"
                  type="number"
                  min="0"
                  max={taskToComplete.requestedQty}
                  value={form.pickedQty}
                  onChange={(e) => setForm((prev) => ({ ...prev, pickedQty: e.target.value }))}
                />
                {parseFloat(form.pickedQty) < taskToComplete.requestedQty && (
                  <p className="flex items-center gap-1 text-sm text-status-warning-foreground">
                    <AlertCircle className="h-4 w-4" />
                    Short pick, less than requested
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="taskNotes">Notes</Label>
                <Textarea
                  id="taskNotes"
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Anything worth recording about this pick"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskToComplete(null)}>
              Cancel
            </Button>
            <Button onClick={completeTask}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Confirm pick
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.main>
  );
}
